import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { listFavorites, toggleFavorite } from '@/services/api/endpoints';
import type { Container } from '@/types/domain';

// Per-user favorite containers, stored server-side so they follow the user
// across web and desktop. Returned as full Container objects (same shape as
// useContainers) so the home "Favoris" section is self-contained.
export function useFavorites() {
  return useQuery<Container[]>({
    queryKey: ['favorites'],
    queryFn: listFavorites,
    staleTime: 60_000,
    retry: 2,
  });
}

// Convenience: the set of favorited container ids, for O(1) "is this favorited?"
// checks in rows/menus without re-deriving on every render.
export function useFavoriteIds(): Set<string> {
  const { data } = useFavorites();
  return new Set((data ?? []).map((c) => c.id));
}

export function useToggleFavorite() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { containerId: string; favorite: boolean }) => toggleFavorite(input),
    // Optimistic: flip the favorite in the cached list immediately so the star
    // and the home section react without waiting for the round-trip.
    onMutate: async ({ containerId, favorite }) => {
      await qc.cancelQueries({ queryKey: ['favorites'] });
      const previous = qc.getQueryData<Container[]>(['favorites']) ?? [];
      if (favorite) {
        const containers = qc.getQueryData<Container[]>(['containers']) ?? [];
        const target = containers.find((c) => c.id === containerId);
        if (target && !previous.some((c) => c.id === containerId)) {
          qc.setQueryData<Container[]>(['favorites'], [target, ...previous]);
        }
      } else {
        qc.setQueryData<Container[]>(
          ['favorites'],
          previous.filter((c) => c.id !== containerId),
        );
      }
      return { previous };
    },
    onError: (_err, _input, ctx) => {
      if (ctx?.previous) qc.setQueryData(['favorites'], ctx.previous);
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: ['favorites'] });
    },
  });
}
